import { type ShopfrontThunk } from "#/Utilities/Generic/ActionsHelper.ts";
import { sendSocketMessage } from "#/Workers/Socket/SocketAgent.ts";
import { SOCKET_EVENTS } from "#/Workers/Socket/SocketEvents.ts";
import Request, { expectPaymentRequired } from "../../Utilities/Platform/Request";
import {
    NOTIFICATION_ALL_DELETE,
    NOTIFICATION_ALL_READ,
    NOTIFICATION_DELETED,
    NOTIFICATION_INSERT,
    NOTIFICATION_READ,
    NOTIFICATIONS_FAILURE,
    NOTIFICATIONS_FETCHED,
    NOTIFICATIONS_FETCHING,
} from "./NotificationConstants";

export interface NotificationType {
    id: string;
    data: {
        vendor: string;
        for: "vendor" | "outlet" | "register" | "user";
        image: string | null;
        title: string;
        message: string;
        action: "link" | "download";
        address: string;
    };
    readAt: string | null;
    createdAt: string;
    deleted?: boolean;
}

interface NotificationsFetchedAction {
    type: typeof NOTIFICATIONS_FETCHED;
    notifications: Array<NotificationType>;
}

export interface NotificationsFetchingAction {
    type: typeof NOTIFICATIONS_FETCHING;
}

export interface NotificationsFailureAction {
    type: typeof NOTIFICATIONS_FAILURE;
    error: string;
}

export interface NotificationDeletedAction {
    type: typeof NOTIFICATION_DELETED;
    id: string;
}

export interface NotificationInsertAction {
    type: typeof NOTIFICATION_INSERT;
    data: NotificationType;
}

export interface NotificationAllReadAction {
    type: typeof NOTIFICATION_ALL_READ;
}

export interface NotificationAllDeleteAction {
    type: typeof NOTIFICATION_ALL_DELETE;
}

export interface NotificationReadAction {
    type: typeof NOTIFICATION_READ;
    id: string;
}

export type NotificationActionTypes = NotificationsFetchedAction |
    NotificationsFetchingAction |
    NotificationsFailureAction |
    NotificationDeletedAction |
    NotificationInsertAction |
    NotificationAllReadAction |
    NotificationAllDeleteAction |
    NotificationReadAction;

export type NotificationList = Array<NotificationType>;

export type NotificationState = {
    loading: boolean;
    notifications: NotificationList;
};

// Functions
function notificationsFetched(notifications: Array<NotificationType>): NotificationActionTypes {
    return {
        type         : NOTIFICATIONS_FETCHED,
        notifications: notifications,
    };
}

function notificationsFailed(error: string): NotificationActionTypes {
    return {
        type: NOTIFICATIONS_FAILURE,
        error,
    };
}

export function notificationDeleted(id: string, broadcast = true): NotificationActionTypes {
    if(broadcast) {
        sendSocketMessage(SOCKET_EVENTS.DELETE_NOTIFICATION, id);
    }

    return {
        type: NOTIFICATION_DELETED,
        id  : id,
    };
}

export function insertNotification(notification: NotificationType): NotificationActionTypes {
    return {
        type: NOTIFICATION_INSERT,
        data: notification,
    };
}

export function fetchingNotifications(): NotificationActionTypes {
    return {
        type: NOTIFICATIONS_FETCHING,
    };
}

export function fetchNotifications(): ShopfrontThunk<NotificationActionTypes> {
    return (dispatch, getState): void => {
        const state = getState();

        dispatch(fetchingNotifications());

        expectPaymentRequired();
        Request.post<{
            errors?: unknown;
            data: {
                notifications: {
                    edges: Array<{
                        node: {
                            id: string;
                            data: {
                                vendor: string;
                                for: "vendor" | "outlet" | "register" | "user";
                                image: string | null;
                                title: string;
                                message: string;
                                action: "link" | "download";
                                address: string;
                            };
                            readAt: string | null;
                            createdAt: string;
                        };
                    }>;
                };
            };
        }>("/graphql", {
            query: `query GetNotifications($register: ID, $outlet: ID) {
                notifications(
                    register: $register, 
                    outlet: $outlet,
                    first: 50,
                    ordering: [{ sort: createdAt, direction: DESC }]
                ) {
                    edges {
                        node {
                            id,
                            data,
                            readAt,
                            createdAt,
                        },
                    },
                },
            }`,
            variables: {
                register: state.location.register?.id,
                outlet  : state.location.outlet?.id,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                return dispatch(notificationsFetched(
                    response.data.data.notifications.edges.map(({ node }) => node)
                ));
            })
            .catch(error => dispatch(notificationsFailed(error)));
    };
}

export function fetchNotificationsIfRequired(): ShopfrontThunk<NotificationActionTypes> {
    // TODO: Add caching
    return fetchNotifications();
}

export function deleteNotification(id: string): ShopfrontThunk<NotificationActionTypes> {
    return (dispatch): void => {
        Request.post<{
            errors?: unknown;
        }>("/graphql", {
            query: `mutation DeleteNotification($id: String!) {
                deleteNotification(id: $id)
            }`,
            variables: {
                id,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                dispatch(notificationDeleted(id));
            })
            .catch(error => {
                console.error("Error deleting notification", error);
                throw new Error("Notification deletion failure");
            });
    };
}

export function markNotificationAsRead(id: string): ShopfrontThunk<NotificationActionTypes> {
    return (dispatch): void => {
        dispatch({
            type: NOTIFICATION_READ,
            id,
        });

        Request.post<{
            errors?: unknown;
        }>("/graphql", {
            query: `mutation ReadNotification($id: String!) {
                readNotification(id: $id)
            }`,
            variables: {
                id,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }
            })
            .catch(err => {
                console.error("Could not mark notification as read", err);
            });
    };
}

export function markAllNotificationsAsRead(
    outlet: null | string,
    register: null | string
): ShopfrontThunk<NotificationActionTypes> {
    return (dispatch): void => {
        Request.post<{
            errors?: unknown;
        }>("/graphql", {
            query: `mutation ReadAllNotifications($register: ID, $outlet: ID) {
                readAllNotifications(register: $register, outlet: $outlet)
            }`,
            variables: {
                outlet,
                register,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                return dispatch({
                    type: NOTIFICATION_ALL_READ,
                });
            })
            .catch(err => {
                dispatch(notificationsFailed(err));

                throw new Error("Notification reading failure");
            });
    };
}

export function deleteAllNotifications(
    outlet: null | string,
    register: null | string
): ShopfrontThunk<NotificationActionTypes> {
    return (dispatch): void => {
        Request.post<{
            errors?: unknown;
        }>("/graphql", {
            query: `mutation DeleteAllNotifications($register: ID, $outlet: ID) {
                deleteAllNotifications(register: $register, outlet: $outlet)
            }`,
            variables: {
                outlet,
                register,
            },
        })
            .then(response => {
                if(response.data.errors) {
                    throw response.data.errors;
                }

                return dispatch({
                    type: NOTIFICATION_ALL_DELETE,
                });
            })
            .catch(err => {
                dispatch(notificationsFailed(err));

                throw new Error("Notification deletion failure");
            });
    };
}
